// Cursor-context derivation for the IntelliSense Engine orchestrator.
// PRD: Slice "CompletionContext + Statement-Isolation Parser" — given the
// original buffer, an offset, and the dialect id, build a `CompletionContext`
// populated enough for the dialect resolver to dispatch without reaching back
// into Monaco. Alias / CTE binding and snapshot lookup are owned by later
// slices; this file only fills `statementRange`, `positionKind`, `dottedBase`,
// and the `insideStringOrComment` flag.
//
// Strategy:
//   1. `findEnclosingStatement` slices the buffer down to one statement, so
//      multi-statement buffers don't cross-pollute clause state.
//   2. Pure-text string/comment scan via `editorContextScan` short-circuits
//      early — the orchestrator hides the popup there. (GRA-7 Slice A2: this
//      replaced a tree-sitter walk over `Tree`/`Node` that lived in
//      `parser/stringCommentMask.ts`; the answer is the same and the
//      derivation no longer needs a parsed tree.)
//   3. `extractDottedBase` does a backward source scan; when present,
//      position is forced to `'dotted-access'`.
//   4. Position-kind classification comes from the sql-grammar model's
//      `computeClauseContext` + the `sqlClauseToCpk` adapter (GRA-7 Slice
//      A6 part 4). The model is now the single source of truth for clause
//      + paren-frame state; the previous IntelliSense-local
//      `detectPositionKind` walk in `positionKind.ts` was removed by this
//      slice. We continue to use IntelliSense's `findEnclosingStatement`
//      for the statement boundary (Oracle accepts a line-leading `/` as a
//      separator and the model's boundary scanner only knows about `;`)
//      and IntelliSense's `extractDottedBase` for the dotted-base shape
//      (it preserves per-part `quoted` info that resolvers branch on; the
//      model's projection strips it).

import {
  findEnclosingDollarQuotedBody,
  isInsideBlockComment,
  isInsideLineComment,
  isInsideStringLiteral,
} from '@/features/sql-grammar/document/editorContextScan';
import { computeClauseContext } from '@/features/sql-grammar/document/editorContextClause';
import { resolveSlot } from '@/features/sql-grammar/document/editorContextSlot';
import {
  DDL_FAMILY_SQL_ENGINES,
  DDL_SLOT_KINDS,
} from '@/features/sql-grammar/document/editorContextSlot.ddl2';

import type {
  CompletionContext,
  DatabaseEngineId,
  OffsetRange,
  ScriptScope,
} from '../../../core/engines';
import { resolveAliasBindings } from '../resolver/aliasScope';
import { resolveCteBindings } from '../resolver/cteScope';
import { databaseEngineIdToSqlEngine } from '../versionGating/engineIdMapping';

import { extractDottedBase } from './dottedBase';
import {
  PREDICATE_HEAD_OVERRIDE_CLAUSES,
  predicateHeadRolesForEngine,
} from './predicateHeadCapabilities';
import { slotKindToCpk } from './slotKindToCpk';
import { sqlClauseToCpk } from './sqlClauseToPositionKind';
import { findEnclosingStatement } from './statementBoundaries';
import {
  VALUE_SLOT_OVERRIDE_CLAUSES,
  valueSlotRolesForEngine,
} from './valueSlotCapabilities';

// GRA-8 cross-engine rollout — engines whose resolvers dispatch on the slot
// seam. mssql was the pilot; pgsql/mysql/oracle/sqlite join as their
// per-engine recognizers + resolver branches land. 'mock' stays out (no real
// grammar). NOTE: an engine may be in this set before it has any recognizer —
// resolveSlot then returns kind 'none' and behavior is unchanged. Membership
// here only makes the gate REACHABLE; the recognizer + resolver branch are
// what produce a win.
const SLOT_SEAM_ENGINES: ReadonlySet<DatabaseEngineId> = new Set<DatabaseEngineId>([
  'mssql', 'pgsql', 'mysql', 'oracle', 'sqlite',
]);

// plan19 T7 — the DDL-override gate below reuses the DDL family's OWN membership
// constants (review Q4: single source, no duplicated engine set, no `ddl-`
// prefix matching): `DDL_FAMILY_SQL_ENGINES` names the four flip engines whose
// resolvers used to run the string-walk `classifyDdlPositionKind` override
// AFTER `deriveCompletionContext` (SqlEngine vocabulary — pgsql maps to
// 'postgres'; mssql never had the override and stays `'unknown'`-gated), and
// `DDL_SLOT_KINDS` enumerates the slot kinds that may win over a named clause.
// Why named clauses at all: the retired override fired on ANY clause-derived
// positionKind, so a DDL slot could win over a named clause too (e.g.
// `INSERT … ON CONFLICT (▮` is clause `insert-values` → `'values'`, and
// `CREATE INDEX … USING ▮` is clause `join-on`). The family recognizes only
// CREATE/ALTER DDL + ON CONFLICT shapes, so non-DDL positions are unaffected —
// the 808-checkpoint DML net holds by construction (see `slotNoWorse.test.ts`).

export function deriveCompletionContext(
  text: string,
  offset: number,
  engineId: DatabaseEngineId,
  scriptScope: ScriptScope = {},
): CompletionContext {
  const clampedOffset = Math.max(0, Math.min(text.length, offset));

  // PG routine bodies (`… AS $$ … $$`) are SQL, not string data. Every scan in
  // this function reads a dollar-quoted region as one long literal, which used
  // to leave the whole body unanalyzable: the string short-circuit below fired
  // once the closing tag was typed, the clause machine and the alias resolver
  // saw a single literal token instead of the body's SELECT/FROM, and the
  // result was `positionKind: 'unknown'` with no aliases — no relation list at
  // FROM, and `alias.▮` completing to nothing. Move the analysis window inside
  // the body so the machinery reads its contents as the SQL it is. Offsets stay
  // absolute; only the window start moves.
  const routineBody =
    engineId === 'pgsql' ? findEnclosingDollarQuotedBody(text, clampedOffset) : null;
  const scanFrom = routineBody?.bodyStart ?? 0;
  const statementRange = routineBody
    ? enclosingStatementWithin(text, clampedOffset, engineId, routineBody.bodyStart, routineBody.bodyEnd)
    : findEnclosingStatement(text, clampedOffset, engineId);
  // `databaseEngineIdToSqlEngine` returns `undefined` for the `'mock'` engine
  // (no real grammar). Fall back to `'postgres'` — the most permissive dialect
  // for string/comment lexing — so the mock test engine still gets sane
  // string/comment classification on the universal `'…'` / `--` / `/* */`
  // constructs.
  const sqlEngine = databaseEngineIdToSqlEngine(engineId) ?? 'postgres';
  const insideStringOrComment =
    isInsideStringLiteral(text, clampedOffset, sqlEngine, scanFrom) ||
    isInsideLineComment(text, clampedOffset, scanFrom) ||
    isInsideBlockComment(text, clampedOffset, scanFrom);
  const cteBindings = resolveCteBindings(text, statementRange, engineId);
  const aliasBindings = resolveAliasBindings(text, statementRange, clampedOffset, engineId, cteBindings);

  const baseContext: Omit<CompletionContext, 'positionKind' | 'dottedBase'> = {
    engineId,
    offset: clampedOffset,
    statementRange,
    aliasBindings,
    cteBindings,
    scriptScope,
    insideStringOrComment,
  };

  if (insideStringOrComment) {
    return { ...baseContext, positionKind: 'unknown' };
  }

  const dottedBase = extractDottedBase(text, clampedOffset, statementRange.start);
  if (dottedBase) {
    return { ...baseContext, positionKind: 'dotted-access', dottedBase };
  }

  // GRA-7 Slice A6 part 4 — position-kind classification is now sourced
  // from the sql-grammar model's clause + paren-frame state machine and
  // mapped through the `sqlClauseToCpk` adapter. The model walks the same
  // statement region (we pass `statementRange.start` as the bound) so the
  // clause state is computed over the same text IntelliSense's previous
  // local `detectPositionKind` operated on.
  const clauseContext = computeClauseContext(text, statementRange.start, clampedOffset, sqlEngine);
  const clausePositionKind = sqlClauseToCpk(clauseContext?.clause ?? null, /* hasDottedBase */ false);

  // GRA-8 slot seam (plan19 T7 shape + value-slot plan T5b + predicate-head
  // plan P3) — consult the fine-grained slot classifier for
  // `SLOT_SEAM_ENGINES` and, if it maps to a CPK, override the clause result
  // under one of FOUR gates (the header comment above
  // `deriveCompletionContext` documents the split):
  //   - Unknown-gate (all seam engines, the original GRA-8 rule): a NAMED
  //     slot refines an `'unknown'` clause result only — it never overrides a
  //     confidently-named position. This is mssql's ONLY named-slot gate.
  //   - DDL-override (the four flip engines × the DDL family's slot kinds):
  //     a DDL slot kind wins over a named clause too, replicating the retired
  //     resolver-side string-walk override (which fired on every clause arm).
  //   - Capability gate (value-slot plan T5b, D4) — `'scalar-value'` ONLY,
  //     handled exclusively here (it never flows through the unknown/DDL
  //     gates): the slot passes when the engine's capability set
  //     (`valueSlotCapabilities.ts`) has the slot's role AND (the clause is
  //     in the role's override set OR the clause result is `'unknown'`) —
  //     the capability check governs BOTH paths. All five engines are live
  //     for the T13–T17 roles.
  //   - Predicate-head capability gate (predicate-head plan P3, PH-D2) —
  //     `'predicate-head'` ONLY, same shape as the scalar-value gate over
  //     `predicateHeadCapabilities.ts`. CRITICAL dormancy invariant: this
  //     kind must NEVER fall through to the unknown/DDL gates — the
  //     recognizer is syntax-only (bare `ON ▮` fires it at an `'unknown'`
  //     clause), so routing it through the unknown-gate would classify it
  //     with the production capability table still EMPTY. The dedicated arm
  //     below returns nothing when the capability check fails, so the
  //     function falls through to the clause result — byte-identical to
  //     pre-P3 behavior until per-engine flips (P7/P8a–d).
  // The slot is a pure sync text scan returning kind `'none'` on anything
  // unrecognised. `sqlEngine` is safe as the engine key here: 'mock' (whose
  // sqlEngine falls back to 'postgres') never reaches this block — it is not
  // in SLOT_SEAM_ENGINES.
  if (SLOT_SEAM_ENGINES.has(engineId)) {
    const slot = resolveSlot({
      source: text,
      stmtStart: statementRange.start,
      offset: clampedOffset,
      engine: sqlEngine,
      clause: clauseContext?.clause ?? null,
    });
    const slotPositionKind = slotKindToCpk(slot.kind);
    if (slotPositionKind) {
      if (slot.kind === 'scalar-value') {
        const role = slot.valueSlot.role;
        if (valueSlotRolesForEngine(engineId).has(role)) {
          const clause = clauseContext?.clause ?? null;
          const isClauseOverride =
            clause !== null && VALUE_SLOT_OVERRIDE_CLAUSES.get(role)?.has(clause) === true;
          if (isClauseOverride || clausePositionKind === 'unknown') {
            return {
              ...baseContext,
              positionKind: slotPositionKind,
              classifiedBy: 'slot',
              valueSlot: slot.valueSlot,
            };
          }
        }
      } else if (slot.kind === 'predicate-head') {
        const role = slot.predicateHead.role;
        if (predicateHeadRolesForEngine(engineId).has(role)) {
          const clause = clauseContext?.clause ?? null;
          const isClauseOverride =
            clause !== null && PREDICATE_HEAD_OVERRIDE_CLAUSES.get(role)?.has(clause) === true;
          // C1 remediation (Fable finding 1 + sol P1, independently found).
          // 'on-head' does NOT take the unknown-clause path. `ON` is heavily
          // overloaded in SQL; at C1 time the recognizer was syntax-only and
          // fired on ANY `ON` directly preceding the cursor, so every
          // object-target `ON` — `CREATE INDEX ix ON ▮`, `GRANT ALL ON ▮`,
          // `COMMENT ON ▮`, `CREATE POLICY p ON ▮`, `DROP INDEX i ON ▮`,
          // `REFERENCES p ON ▮` — reached this gate at an 'unknown' clause.
          // Those positions want exactly the relation and object names that a
          // predicate head's FORBIDDEN_KINDS suppresses, so promoting them
          // would replace the right answer with head keywords.
          //
          // Nothing is lost by excluding it: every genuine join/MERGE `ON`
          // yields the NAMED clause 'join-on' (JOIN → from → join-on; USING →
          // join-on) and passes through isClauseOverride above. The unknown
          // path carried no true positive for this role at all.
          //
          // C3 update: the converse of C1's argument was FALSE — a NAMED
          // 'join-on' clause is not exclusively a genuine join (the clause
          // machine names ANY `ON` met in a 'from' frame 'join-on', so an
          // upsert `INSERT … SELECT … FROM src ON ▮CONFLICT` passed the
          // override above and got head-promoted). Fixed at the RECOGNIZER:
          // 'on-head' now requires a genuine unconsumed JOIN/MERGE
          // association (`editorContextSlot.predicateHeadOn.ts`), which also
          // refuses the object-target shapes before they reach this gate.
          // This bar stays as defense in depth behind it.
          //
          // where-head/having-head keep it: their clause keyword always sets
          // its named clause, so the path is inert for them today, but it
          // stays available for procedural/embedded shapes where the clause
          // machine yields nothing.
          const unknownPathAllowed = role !== 'on-head';
          if (isClauseOverride || (unknownPathAllowed && clausePositionKind === 'unknown')) {
            return {
              ...baseContext,
              positionKind: slotPositionKind,
              classifiedBy: 'slot',
              predicateHead: slot.predicateHead,
            };
          }
        }
      } else {
        const isUnknownGate = clausePositionKind === 'unknown';
        const isDdlOverride =
          DDL_FAMILY_SQL_ENGINES.has(sqlEngine) && DDL_SLOT_KINDS.has(slot.kind);
        // mssql carries ONE named-clause override of its own:
        // 'ddl-index-column'. T-SQL spells the index target `… ON tbl (▮`, so
        // the clause machine confidently names that caret 'from' / 'join-on'
        // and the unknown-gate above can never reach it — the index's column
        // list answered with the relation list (script.31 §3-§6) while the
        // four family engines got the columns. Named explicitly (not by a
        // `ddl-` prefix or the whole DDL_SLOT_KINDS set) so no other slot kind
        // silently gains override power on mssql.
        const isMssqlIndexColumnOverride =
          sqlEngine === 'mssql' && slot.kind === 'ddl-index-column';
        // The SECOND named-clause override, same precedent and same
        // narrowness as the mssql index-column one above: a row-source
        // COLUMNS entry's data-type slot. `JSON_TABLE(… COLUMNS (tag ▮ PATH …))`,
        // `XMLTABLE(… COLUMNS (n ▮ …))` and T-SQL `OPENJSON(…) WITH (n ▮ …)`
        // all live inside a FROM clause, so the clause machine confidently
        // names the caret 'from' and the unknown-gate can NEVER reach it —
        // the type slot answered with the relation list (mysql coverage
        // script.43 §4). Cross-engine because the syntax is (MySQL + Oracle
        // JSON_TABLE, Oracle + PG XMLTABLE, T-SQL OPENJSON WITH); an engine
        // without it never produces the kind.
        //
        // Named by its own slot KIND rather than by `'data-type'`, so no
        // other data-type caret gains override power: an ordinary
        // `CAST(x AS ▮` or `CREATE TABLE t (id ▮` still has to come through
        // the unknown-gate exactly as before. The recognizer refuses every
        // shape that is not nested inside a row-source call's own parens
        // (`PARTITION BY RANGE COLUMNS (…)`, `GRAPH_TABLE(… COLUMNS (…)`),
        // so a `from` caret outside one classifies byte-identically — pinned
        // in `slotNoWorse.test.ts`.
        const isRowSourceColumnTypeOverride = slot.kind === 'row-source-column-type';
        if (
          isUnknownGate
          || isDdlOverride
          || isMssqlIndexColumnOverride
          || isRowSourceColumnTypeOverride
        ) {
          return { ...baseContext, positionKind: slotPositionKind, classifiedBy: 'slot' };
        }
      }
    }
  }

  return { ...baseContext, positionKind: clausePositionKind, classifiedBy: 'clause' };
}

/** `findEnclosingStatement` restricted to a nested region — a PG routine body,
 *  whose own `;`-separated statements are what the cursor is really inside.
 *  Runs the normal scan over the body slice and lifts the result back to
 *  absolute offsets. */
function enclosingStatementWithin(
  text: string,
  offset: number,
  engineId: DatabaseEngineId,
  bodyStart: number,
  bodyEnd: number,
): OffsetRange {
  const inner = findEnclosingStatement(
    text.slice(bodyStart, bodyEnd),
    offset - bodyStart,
    engineId,
  );
  return { start: bodyStart + inner.start, end: bodyStart + inner.end };
}
